import { Component, DestroyRef, ElementRef, HostListener, ViewChild, NgZone, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, Subject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { UserService } from '@app/services';
import { AuthService } from '@app/services';
import { AccountManagerService, SavedAccount } from '@app/services';
import { AlertComponent } from '@app/shared/alert/alert.component';

type SignInResponse =
  | { error: { message?: string } }
  | { error?: undefined };

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [FormsModule, CommonModule, AlertComponent],
  templateUrl: './login-page.component.html'
})
export class LoginPageComponent {
  private readonly unverifiedAccountMessage = '你的帳號尚未驗證或發生錯誤，相關解決辦法請聯繫管理員！';

  phone: string = '';
  isLoading = false;
  password: string = '';
  failMessage = '';
  staticAlertClosed = false;
  fontSize: number = 18;
  showPassword: boolean = false;

  // 多帳號切換相關屬性
  savedAccounts: SavedAccount[] = [];
  showAccountList = false;
  rememberAccount = false; // 預設不勾選記住帳號（但一定會記住帳號）

  private _failMessage$ = new Subject<string>();
  private _isLoading$ = new Subject<boolean>();

  showModal: boolean = false;

  // Template 需加上 #accountDropdown、#phoneInput、#toggleButton
  @ViewChild('accountDropdown', { read: ElementRef }) accountDropdown?: ElementRef<HTMLElement>;
  @ViewChild('phoneInput', { read: ElementRef }) phoneInput?: ElementRef<HTMLElement>;
  @ViewChild('toggleButton', { read: ElementRef }) toggleButton?: ElementRef<HTMLElement>;

  constructor(
    private userService: UserService, 
    private authService: AuthService, 
    private router: Router,
    private accountManager: AccountManagerService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {
    this._failMessage$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((message) => {
          this.failMessage = message;
          // 有訊息就顯示，沒有就關閉
          this.staticAlertClosed = !message;
        }),
        // 自動在 2 秒後關閉提示
        debounceTime(2000)
      )
      .subscribe(() => {
        this.staticAlertClosed = true;
        this.cdr.markForCheck();
      });

    this._isLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => {
        this.isLoading = loading;
        this.cdr.markForCheck();
      });
  }

  async ngOnInit() {
    const storedFontSize = localStorage.getItem('fontSize');
    if (storedFontSize) {
      this.fontSize = parseInt(storedFontSize, 10);
    }

    // 訂閱保存的帳號列表（加上 takeUntilDestroyed 防洩漏）
    this.accountManager.savedAccounts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(accounts => {
        this.savedAccounts = [...accounts].sort((a, b) => b.lastLoginTime - a.lastLoginTime);
        
        // 如果有保存的帳號且當前沒有輸入電話號碼，預填第一個
        if (this.savedAccounts.length > 0 && !this.phone) {
          this.selectSavedAccount(this.savedAccounts[0]);
        }
        this.cdr.markForCheck();
      });
  }

  // 選擇保存的帳號
  selectSavedAccount(account: SavedAccount) {
    this.phone = this.toLocalDisplay(account.phone);
    this.password = ''; // 清空密碼欄位
    this.showAccountList = false;
    this.accountManager.setCurrentAccount(account);
    this.clearError();
  }

  // 切換帳號列表顯示
  toggleAccountList() {
    this.showAccountList = !this.showAccountList;
    if (this.showAccountList) this.clearError();
  }

  // 移除保存的帳號
  removeSavedAccount(event: Event, phone: string) {
    event.stopPropagation();
    this.accountManager.removeAccount(phone);
  }

  // 使用其他帳號
  useOtherAccount() {
    this.phone = '';
    this.password = '';
    this.showAccountList = false;
    this.clearError();
  }

  // 電話號碼輸入處理
  onPhoneInput() {
    this.clearError();
    // 關閉帳號選擇列表如果用戶開始輸入
    if (this.showAccountList) {
      this.showAccountList = false;
    }
  }

  // 密碼輸入處理
  onPasswordInput() {
    this.clearError();
  }

  // 切換密碼顯示/隱藏
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  // 監聽點擊事件，點擊外部時關閉下拉選單（用 ViewChild 比 querySelector 穩）
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.showAccountList) return;

    const target = event.target as Node;
    const inDropdown = this.accountDropdown?.nativeElement.contains(target);
    const inPhone = this.phoneInput?.nativeElement.contains(target);
    const inToggle = this.toggleButton?.nativeElement.contains(target);

    if (!inDropdown && !inPhone && !inToggle) {
      this.showAccountList = false;
      this.cdr.markForCheck();
    }
  }

  async login() {
    // Set loading immediately when button is clicked
    this._isLoading$.next(true);
    
    this.clearError();

    // Electric phone format validation and conversion
    const phoneNumber = this.formatPhoneNumber(this.phone);
    if (!phoneNumber) {
      this.showError('請輸入有效的台灣電話號碼格式\n手機：0912345678\n市話：02-12345678 或 03-1234567');
      this._isLoading$.next(false);
      return;
    }

    // Password validation
    if (!this.password) {
      this.showError('請輸入密碼');
      this._isLoading$.next(false);
      return;
    }

    if (this.password.length < 6) {
      this.showError('密碼長度不得少於6個字元');
      this._isLoading$.next(false);
      return;
    }

    try {
      const res = (await this.authService.signInWithMultiDevice(phoneNumber, this.password)) as SignInResponse;
      
      if (res?.error) {
        // 根據不同的錯誤類型提供更詳細的錯誤訊息
        const msg = (res.error.message || '').toLowerCase();
        
        if (msg.includes('invalid login credentials') || 
            msg.includes('invalid email or password')) {
          this.showError('帳號或密碼錯誤，請檢查後重新輸入');
        } else if (msg.includes('email not confirmed') ||
                   msg.includes('phone not confirmed')) {
          this.showError(this.unverifiedAccountMessage);
        } else if (msg.includes('too many requests') || 
                   msg.includes('rate limit')) {
          this.showError('登入嘗試次數過多，請稍後再試');
        } else if (msg.includes('network') ||
                   msg.includes('timeout')) {
          this.showError('網路連線異常，請檢查網路狀態後重試');
        } else if (msg.includes('user not found')) {
          this.showError('此帳號不存在，請確認手機號碼或前往註冊');
        } else if (msg.includes('account locked') || 
                   msg.includes('locked')) {
          this.showError('帳號已被鎖定，請聯繫客服：0983-408335');
        } else {
          this.showError('登入失敗，請檢查帳號密碼是否正確');
        }
        return;
      }
      
      const isConfirmed = await this.userService.checkIsConfirm();
      if (isConfirmed) {
        // 一定會記住帳號（不含密碼）
        try {
          const userRes = await this.userService.getUser();
          if (userRes?.data?.[0]) {
            // 勾選記住帳號時保存這台裝置的 session 供快速切換；不保存密碼
            await this.accountManager.saveAccount(userRes.data[0], phoneNumber, this.rememberAccount);
          }
        } catch (saveError) {
          console.warn('Failed to save account:', saveError);
          // 不影響登入流程，只記錄警告
        }
        // SPA 導頁（避免整頁 reload）
        this.router.navigateByUrl('/home');
      } else {
        this.showError(this.unverifiedAccountMessage);
        await this.userService.signOut();
      }
    } catch (error: unknown) {
      // 處理不同類型的異常
      const message = (error as { message?: string })?.message?.toLowerCase() || '';
      if (message.includes('network') || 
          message.includes('fetch') ||
          message.includes('timeout')) {
        this.showError('網路連線問題，請檢查網路狀態後重試');
      } else if (message.includes('cors')) {
        this.showError('服務暫時無法使用，請稍後再試');
      } else {
        this.showError('登入過程中發生未預期的錯誤，請重新嘗試');
      }
    } finally {
      this._isLoading$.next(false);
    }
  }

  goToRegister() {
    this.router.navigate(['/register']);
  }

  openModal() {
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
  }

  // ===== 共用：錯誤顯示（在 Zone 內 + 標記變更） =====
  private showError(message: string) {
    this.zone.run(() => {
      this._failMessage$.next(message);
      this.staticAlertClosed = false;
      this.cdr.markForCheck();
    });
  }

  private clearError() {
    this.zone.run(() => {
      this._failMessage$.next('');
      this.staticAlertClosed = true;
      this.cdr.markForCheck();
    });
  }

  /**
   * 格式化並驗證台灣電話號碼（包含手機和市內電話）
   * @param phone 輸入的電話號碼
   * @returns 格式化後的國際格式電話號碼，或 null（如果格式無效）
   */
  private formatPhoneNumber(phone: string): string | null {
    if (!phone) return null;

    // 標準化：移除空白/破折號/括號，保留前導 +
    const clean = phone.replace(/[()\s-]/g, '').trim();
    
    // 如果已經是國際格式，直接驗證
    if (clean.startsWith('+886')) {
      const numberPart = clean.substring(4);
      if (/^9\d{8}$/.test(numberPart)) return clean;            // 手機 9 + 8 碼
      if (/^[2-8]\d{6,7}$/.test(numberPart)) return clean;      // 市話
      return null;
    }
    
    // 886 開頭但沒有 +
    if (clean.startsWith('886')) {
      const numberPart = clean.substring(3);
      if (/^9\d{8}$/.test(numberPart) || /^[2-8]\d{6,7}$/.test(numberPart)) {
        return `+${clean}`;
      }
      return null;
    }
    
    // 台灣手機號碼格式：09xxxxxxxx
    if (/^09\d{8}$/.test(clean)) return `+886${clean.substring(1)}`;
    
    // 台北：02-xxxxxxxx (8位數字)
    if (/^02\d{8}$/.test(clean)) return `+886${clean.substring(1)}`;
    
    // 其他地區：03/04/05/06/07/08-xxxxxxx (7或8位數字)
    if (/^0[3-8]\d{7,8}$/.test(clean)) return `+886${clean.substring(1)}`;
    
    return null;
  }

  /** 將 +886... 轉回本地顯示格式 */
  private toLocalDisplay(e164: string): string {
    if (!e164?.startsWith('+886')) return e164 || '';
    const n = e164.substring(4);
    if (n.startsWith('9') && n.length === 9) return `09${n.substring(1)}`;
    if (n.startsWith('2')) return `02-${n.substring(1)}`;
    if (/^[3-8]\d{6,7}$/.test(n)) return `0${n.charAt(0)}-${n.substring(1)}`;
    return `0${n}`;
  }
}
